import { type TenantData } from '@/types/tenant';

export const AUTH0_AUDIENCE = 'https://internal.api.dam.lookbooks.com';

/**
 * The Auth0Provider props shared by both provider instances in `_app.tsx`
 * (public routes and authenticated routes). Only `onRedirectCallback` differs
 * between them, so it stays at the call site.
 *
 * `useRefreshTokens` is what stops a long batch upload from bouncing the user to
 * the login screen: without it the SDK renews an expired access token through a
 * hidden `/authorize?prompt=none` iframe, which Safari's ITP starves of the Auth0
 * session cookie. Auth0 answers `login_required`, auth0-spa-js logs the user out
 * of its own cache, and AuthWall navigates away mid-upload (DM-2533).
 *
 * `useRefreshTokensFallback` covers users who logged in before this shipped:
 * `offline_access` is part of the cache key, so their cached entry no longer
 * matches and has no refresh token. Without the fallback they get
 * MissingRefreshTokenError on every request with no way out.
 *
 * Do not add `offline_access` to `scope` by hand — the SDK injects it when
 * `useRefreshTokens` is on, and a hand-set scope risks a cache-key mismatch.
 */
export const buildAuth0ProviderProps = (tenantData: TenantData) => ({
  domain: process.env.NEXT_PUBLIC_AUTH_DOMAIN!,
  clientId: process.env.NEXT_PUBLIC_AUTH_CLIENT_ID!,
  authorizationParams: {
    audience: AUTH0_AUDIENCE,
    organization: tenantData.tenant.auth0.organizationId,
    redirect_uri: typeof window === 'undefined' ? undefined : window.location.origin,
  },
  cacheLocation: 'localstorage' as const,
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
});

/**
 * Builds the `/authorize` redirect URL used by the `/login` bounce in `_app.tsx`.
 * `host` must be `window.location.host` (not `hostname`) so the redirect follows
 * whatever port the app is actually served on.
 */
export const buildLoginRedirectUrl = ({
  authDomain,
  clientId,
  search,
  protocol,
  organizationName,
  host,
}: {
  authDomain: string;
  clientId: string;
  search: string;
  protocol: string;
  organizationName: string | null;
  host: string;
}) =>
  `https://${authDomain}/authorize${search}&response_type=code&client_id=${clientId}&redirect_uri=${protocol}//${organizationName}.${host}`;
